import './pwaInstall'; // Capture beforeinstallprompt before anything renders
import * as Sentry from '@sentry/react';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { initMixpanel } from './lib/mixpanel';
import { captureGeneratedLink } from './lib/captureGeneratedLink';
import { isTelegramWebApp } from './lib/platform';
import { ready as telegramReady } from './lib/telegram';
import './i18n';
import './index.css';

// Tell the Telegram host we're ready and put the webview into the
// largest viewport it'll give us (ready + expand + requestFullscreen).
// See lib/telegram.ts ready() for the per-call rationale. No-op in PWA.
if (isTelegramWebApp()) {
  telegramReady();
}

// Capture acquisition slug from ?link= BEFORE React renders. React Router's
// root-route <Navigate to="/home" replace /> would otherwise strip the query
// string via its useLayoutEffect before our App-level useEffect could read it.
captureGeneratedLink();

// Initialize analytics before render
initMixpanel();

Sentry.init({
  dsn: 'https://b032dc9cbfc349575c7752c32c00aa15@o4510872700780544.ingest.de.sentry.io/4510872708317264',
  tunnel: '/api/sentry-tunnel',
  sendDefaultPii: true,
});

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);

// Check for service worker updates when the app comes back to foreground.
// Combined with registerType: 'autoUpdate', this ensures users always get
// the latest version without manual cache clearing.
if ('serviceWorker' in navigator) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      navigator.serviceWorker.ready.then((registration) => {
        registration.update();
      });
    }
  });
}
